import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from "@mui/lab";
import {
  Box,
  Pagination,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { dispatch } from "store";
import {
  getFillingPersonStockLogsThunk,
  fillingPersonStockLogSelector,
} from "store/reducers/inventory/stock/fillingPersonStockSlice";
import NorthIcon from "@mui/icons-material/North";
import SouthIcon from "@mui/icons-material/South";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  DesktopDateTimePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import AutoCompleteList from "components/AutoCompleteList";
import { getFillingPersonThunk } from "store/reducers/accounts";
import { Link, useSearchParams } from "react-router-dom";

const FillingPersonStockLogs = () => {
  const [page, setPage] = useState(1);
  const [searchParams] = useSearchParams();
  const fillingPerson = searchParams.get("fillingPerson");
  const pageCount = useSelector((state) => state.fillingPersonStock.logPageCount);
  const logs = useSelector(fillingPersonStockLogSelector.selectAll);
  const fillingPersons = useSelector((state) => state.accounts.fillingPersons);
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const [filter, setFilter] = useState({
    from_date: null,
    to_date: null,
    fillingPerson: null,
  });

  useEffect(() => {
    dispatch(getFillingPersonThunk());
  }, []);

  useEffect(() => {
    if (fillingPerson && fillingPersons) {
      const dist = fillingPersons.find(
        (item) => item.id === parseInt(fillingPerson)
      );
      setFilter((state) => ({ ...state, fillingPerson: dist }));
    }
  }, [fillingPerson, fillingPersons]);

  const getFillingPersonStockLogs = useCallback(() => {
    dispatch(
      getFillingPersonStockLogsThunk({
        page: page,
        from_date: moment(filter.from_date).isValid()
          ? moment(filter.from_date).toISOString()
          : "",
        to_date: moment(filter.to_date).isValid()
          ? moment(filter.to_date).toISOString()
          : "",
        fillingPerson: filter.fillingPerson?.profile?.id ?? "",
      })
    );
  }, [filter.fillingPerson, filter.from_date, filter.to_date, page]);
  useEffect(() => {
    getFillingPersonStockLogs();
  }, [getFillingPersonStockLogs, page]);

  const handlePageChange = (event, value) => {
    setPage(value);
    getFillingPersonStockLogs();
  };
  const getTimeDot = (direction) => {
    switch (direction) {
      case "in":
        return (
          <TimelineDot variant="filled" color="success">
            <SouthIcon fontSize="small" />
          </TimelineDot>
        );
      case "out":
        return (
          <TimelineDot variant="filled" color="info">
            <NorthIcon fontSize="small" />
          </TimelineDot>
        );
      default:
        return (
          <TimelineDot variant="filled" color="warning">
            <ReplayIcon fontSize="small" />
          </TimelineDot>
        );
    }
  };
  return (
    <Box padding={2}>
      <Stack
        direction={isSmallScreen ? "column" : "row"}
        justifyContent="center"
        spacing={1.5}
      >
        <AutoCompleteList
          items={fillingPersons}
          name="username"
          label="Filter filling person"
          setValue={(value) => setFilter({ ...filter, fillingPerson: value })}
          value={filter.fillingPerson}
          fullWidth={true}
        />
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DesktopDateTimePicker
            label="From date"
            value={filter.from_date}
            onChange={(value) =>
              // setFromDate(value)
              setFilter({ ...filter, from_date: value })
            }
            renderInput={(params) => (
              <TextField variant="outlined" fullWidth {...params} />
            )}
          />
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DesktopDateTimePicker
            label="To date"
            value={filter.to_date}
            onChange={(value) => setFilter({ ...filter, to_date: value })}
            renderInput={(params) => (
              <TextField variant="outlined" fullWidth {...params} />
            )}
          />
        </LocalizationProvider>
      </Stack>
      <Typography color="text.secondary" sx={{ textAlign: "center", mt: 2 }}>
        {" "}
        * Click on date to view stock snapshot{" "}
      </Typography>
      <Timeline position="right">
        {logs.map((log) => (
          <TimelineItem key={log.id}>
            <TimelineOppositeContent color="text.secondary">
              <Typography
                color="text.secondary"
                component={Link}
                to={`/filling-allocation/history?time=${moment(
                  log.date
                ).toISOString()}&fillingPerson=${
                  filter.fillingPerson ? filter.fillingPerson.id : ""
                }`}
                sx={{ textDecoration: "none" }}
              >
                {" "}
                {moment(log.date).format("MMMM Do YYYY, h:mm:ss a")}{" "}
              </Typography>
            </TimelineOppositeContent>
            <TimelineSeparator>
              {getTimeDot(log.in_or_out)}
              <TimelineConnector />
            </TimelineSeparator>
            <TimelineContent>
              <Stack direction="column">
                <Typography variant="h6" component="span">
                  {log.name}
                </Typography>
                <Typography variant="caption">
                  {log.qty} Nos, {log.price} Rs per item
                </Typography>
                {log.in_or_out === "out" && (
                  <Typography variant="subtitle1" component="span">
                    [Delivered to {log.machine_name}]
                  </Typography>
                )}
                {log.in_or_out === "in" && log.location_name && (
                  <Typography variant="subtitle1" component="span">
                    [Delivered from {log.location_name}]
                  </Typography>
                )}
                {log.in_or_out === "in" && log.machine_name && (
                  <Typography variant="subtitle1" component="span">
                    [Returned from {log.machine_name}]
                  </Typography>
                )}
                {log.in_or_out === "ret" && (
                  <Typography variant="subtitle1" component="span">
                    [Returned to {log.location_name}]
                  </Typography>
                )}
              </Stack>
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>

      <Stack direction="row" justifyContent="center">
        <Pagination
          count={pageCount}
          variant="outlined"
          shape="rounded"
          onChange={handlePageChange}
        />
      </Stack>
    </Box>
  );
};

export default FillingPersonStockLogs;
